import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { playCardSound, playJollySound, playVictory, playNewRound, initAudio, startBattleMusic, stopAllMusic } from '../lib/SoundManager';
import { useScreenShake, SHAKE_BY_CARD, SCENE } from '../lib/battle3d';
import CardGrid from './CardGrid';
import PlayerAvatar from './PlayerAvatar';
import MusicToggle from './MusicToggle';
import { parseActionType, parseTargetPlayer } from './GameAnimations';
import GameRules from './GameRules';
import CardEffectOverlay from './CardEffectOverlay';
import VictoryScreen from './VictoryScreen';
import BattleField from './battle/BattleField';
import BattleIntro from './battle/BattleIntro';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useOrientation = () => {
  const get = () => {
    const w = window.innerWidth;
    const h = window.innerHeight;
    const isLandscape = w > h;
    const shortSide = Math.min(window.screen.width, window.screen.height);
    const isMobile = shortSide < 768;
    return { isLandscape, isMobile };
  };
  const [state, setState] = useState(get);
  useEffect(() => {
    const handler = () => { setTimeout(() => setState(get()), 150); };
    window.addEventListener('resize', handler);
    window.addEventListener('orientationchange', handler);
    document.addEventListener('visibilitychange', handler);
    return () => {
      window.removeEventListener('resize', handler);
      window.removeEventListener('orientationchange', handler);
      document.removeEventListener('visibilitychange', handler);
    };
  }, []);
  return state;
};

// ── Ribbon that sweeps across to announce a beat of the battle ──
const HERALD_TONES = {
  gold:    { rim: 'rgba(255,215,0,0.6)',  glow: 'rgba(255,180,0,0.35)', bg: 'rgba(52,38,8,0.97)', text: 'linear-gradient(to bottom, #fff, #FFD700 60%, #b8860b)' },
  crimson: { rim: 'rgba(248,113,113,0.6)', glow: 'rgba(220,40,40,0.35)', bg: 'rgba(48,10,8,0.97)', text: 'linear-gradient(to bottom, #fff, #fca5a5 55%, #991b1b)' },
};

const Herald = ({ show, text, tone = 'gold', duration = 2.1, testId = 'turn-herald' }) => {
  const t = HERALD_TONES[tone] || HERALD_TONES.gold;
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key={`herald-${text}`}
          initial={{ opacity: 0, scaleX: 0.2 }}
          animate={{ opacity: [0, 1, 1, 0], scaleX: [0.2, 1, 1, 1.15] }}
          exit={{ opacity: 0 }}
          transition={{ duration, times: [0, 0.18, 0.72, 1] }}
          data-testid={testId}
          style={{
            position: 'fixed', top: '30%', left: 0, right: 0, zIndex: 70,
            pointerEvents: 'none', display: 'flex', justifyContent: 'center',
          }}
        >
          <div style={{
            padding: '8px 44px',
            background: `linear-gradient(90deg, transparent, ${t.bg} 12%, ${t.bg} 88%, transparent)`,
            borderTop: `1px solid ${t.rim}`, borderBottom: `1px solid ${t.rim}`,
            boxShadow: `0 0 40px ${t.glow}`,
          }}>
            <span style={{
              fontFamily: "'Cinzel',serif", fontSize: 'clamp(15px,3.4vw,26px)', fontWeight: 900,
              letterSpacing: '0.24em', textTransform: 'uppercase',
              background: t.text,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              {text}
            </span>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const GameTable = ({ user }) => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [gameState, setGameState] = useState(null);
  const [displayedCards, setDisplayedCards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [revealingCard, setRevealingCard] = useState(null);
  const [showWinner, setShowWinner] = useState(false);
  const [pendingCardRefresh, setPendingCardRefresh] = useState(false);
  const [playerEffects, setPlayerEffects] = useState({});
  const [cardEffect, setCardEffect] = useState(null);
  const [cardEffectText, setCardEffectText] = useState(null);
  const [showIntro, setShowIntro] = useState(() => sessionStorage.getItem(`tlk_intro_${gameId}`) !== 'done');
  const [heraldTurn, setHeraldTurn] = useState(false);
  const pollingIntervalRef = useRef(null);
  const gameChannelRef = useRef(null);
  const broadcastChannelRef = useRef(null);
  const initialLoadDone = useRef(false);
  const audioInitialized = useRef(false);
  const musicStarted = useRef(false);
  const wasMyTurn = useRef(false);
  const { isLandscape, isMobile } = useOrientation();
  const { shakeControls, shake } = useScreenShake();

  const ensureAudio = useCallback(() => {
    if (!audioInitialized.current) { initAudio(); audioInitialized.current = true; }
    if (!musicStarted.current) { startBattleMusic(); musicStarted.current = true; }
  }, []);

  const triggerPlayerEffect = useCallback((playerName, effectType) => {
    if (!playerName || !effectType) return;
    setPlayerEffects(prev => ({ ...prev, [playerName]: effectType }));
    setTimeout(() => {
      setPlayerEffects(prev => { const next = { ...prev }; delete next[playerName]; return next; });
    }, 700);
  }, []);

  const triggerAnimations = useCallback((message, cardValue, playerName) => {
    const actionType = parseActionType(message, cardValue);
    if (!actionType || !playerName) return;
    if (actionType === 'crown_pass') {
      const target = parseTargetPlayer(message);
      triggerPlayerEffect(playerName, 'crown_loss');
      if (target) triggerPlayerEffect(target, 'crown_gain');
    } else if (actionType === 'crown_loss') {
      triggerPlayerEffect(playerName, 'crown_loss');
    } else if (actionType === 'crown_gain') {
      triggerPlayerEffect(playerName, 'crown_gain');
    } else if (actionType === 'shield_block') {
      triggerPlayerEffect(playerName, 'shield_block');
    } else if (actionType === 'jolly') {
      triggerPlayerEffect(playerName, 'jolly');
    }
  }, [triggerPlayerEffect]);

  useEffect(() => { return () => { stopAllMusic(); }; }, []);

  const loadGameState = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/game/${gameId}`);
      const newState = response.data;
      setGameState(newState);
      if (!initialLoadDone.current) {
        setDisplayedCards(newState.cards_on_table);
        initialLoadDone.current = true;
      }
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load game');
    }
  }, [gameId]);

  const subscribeToGameUpdates = useCallback(() => {
    const channel = supabase.channel(`game_${gameId}`);
    channel.on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'game_sessions', filter: `id=eq.${gameId}` },
      (payload) => { setGameState(payload.new); }
    ).subscribe();
    gameChannelRef.current = channel;

    const broadcastChannel = supabase.channel(`game_actions_${gameId}`);
    broadcastChannel.on('broadcast', { event: 'player_action' }, (payload) => {
      const action = payload.payload;
      if (action.card_effect) {
        const dur = (action.card_effect === 'crown_prev' || action.card_effect === 'crown_next') ? 1800 : action.card_effect === 'jolly_effect' ? 2200 : 1200;
        setCardEffect(action.card_effect);
        setTimeout(() => setCardEffect(null), dur);
      }
      if (action.player_id !== user?.user_id) {
        // Feel the blow even when it lands on someone else — a touch softer.
        const power = SHAKE_BY_CARD[action.card_value] ?? 0;
        if (power) shake(power * 0.6);
        if (action.type === 'jolly') {
          setFeedback({ message: action.message, isJolly: true });
          playJollySound();
          triggerAnimations(action.message, null, action.player_name);
        } else if (action.type === 'card_pick') {
          setFeedback({ message: action.message, cardValue: action.card_value, isJolly: false });
          triggerAnimations(action.message, action.card_value, action.player_name);
        }
        setTimeout(() => setFeedback(null), 2500);
      }
    }).subscribe();
    broadcastChannelRef.current = broadcastChannel;

    return () => {
      channel.unsubscribe(); supabase.removeChannel(channel);
      broadcastChannel.unsubscribe(); supabase.removeChannel(broadcastChannel);
    };
  }, [gameId, user?.user_id, triggerAnimations, shake]);

  const startPolling = useCallback(() => {
    pollingIntervalRef.current = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/game/${gameId}`);
        setGameState(response.data);
        const currentTurnPlayer = response.data.players[response.data.current_turn];
        const playersWhoPlayed = response.data.players_who_played || [];
        if (currentTurnPlayer && (currentTurnPlayer.is_frozen || playersWhoPlayed.includes(currentTurnPlayer.user_id))) {
          try { await axios.post(`${API}/game/auto-advance/${gameId}`); } catch (e) {}
        }
      } catch (error) {}
    }, 3000);
    return () => { if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current); };
  }, [gameId]);

  useEffect(() => {
    loadGameState();
    const unsubscribe = subscribeToGameUpdates();
    const stopPolling = startPolling();
    return () => { unsubscribe(); stopPolling(); };
  }, [loadGameState, subscribeToGameUpdates, startPolling]);

  useEffect(() => {
    if (!gameState) return;
    const serverCards = gameState.cards_on_table || [];
    if (!displayedCards) { setDisplayedCards(serverCards); return; }
    const isSameRound = serverCards.length === displayedCards.length &&
      serverCards.every((c, i) => c.value === displayedCards[i].value);
    if (isSameRound) {
      const needsRevealSync = serverCards.some((c, i) => c.revealed && !displayedCards[i].revealed);
      if (needsRevealSync) {
        const revealedIndices = serverCards.map((c, i) => c.revealed && !displayedCards[i].revealed ? i : null).filter(i => i !== null);
        revealedIndices.forEach(idx => playCardSound(serverCards[idx].value));
        setDisplayedCards(prev => prev.map((card, i) => ({ ...card, revealed: card.revealed || serverCards[i].revealed })));
      }
    } else {
      setPendingCardRefresh(true);
      setTimeout(() => { playNewRound(); setDisplayedCards(serverCards); setPendingCardRefresh(false); }, 1200);
    }
  }, [gameState, displayedCards]);

  // Announce the turn once, the moment it swings to us
  useEffect(() => {
    if (!gameState) return undefined;
    const roster = gameState.players || [];
    const onTurn = roster[gameState.current_turn];
    const me = roster.find(p => p.user_id === user.user_id);
    const alreadyPlayed = (gameState.players_who_played || []).includes(me?.user_id);
    const mine = onTurn?.user_id === user.user_id && !me?.is_frozen && !alreadyPlayed;
    if (mine && !wasMyTurn.current) {
      wasMyTurn.current = true;
      setHeraldTurn(true);
      const t = setTimeout(() => setHeraldTurn(false), 2200);
      return () => clearTimeout(t);
    }
    if (!mine) wasMyTurn.current = false;
    return undefined;
  }, [gameState, user.user_id]);

  useEffect(() => {
    if (gameState?.status === 'finished' && !showWinner) {
      stopAllMusic(); playVictory();
      setTimeout(() => setShowWinner(true), 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState?.status, showWinner]);

  const handlePickCard = async (cardIndex) => {
    if (actionInProgress) return;
    ensureAudio();
    setActionInProgress(true);
    setRevealingCard(cardIndex);
    try {
      const response = await axios.post(`${API}/game/pick-card`, { game_id: gameId, user_id: user.user_id, card_index: cardIndex });
      playCardSound(response.data.card_value);
      const cv = response.data.card_value;
      const effectMap = { 1:'lightning', 2:'poison', 3:'fire', 4:'sparkle', 5:'petals', 6:'butterflies', 7:'shield', 8:'crown_prev', 9:'crown_next', 10:'gold' };
      if (effectMap[cv]) {
        setCardEffect(effectMap[cv]);
        setTimeout(() => setCardEffect(null), cv === 8 || cv === 9 ? 1800 : 1200);
      }
      // The blade lands — shake the battlefield in proportion to the blow.
      const power = SHAKE_BY_CARD[cv] ?? 0;
      if (power) setTimeout(() => shake(power), 700);
      const effectTexts = {1:'⚡ Fulmine — perdi 1 corona',2:'☠ Veleno — perdi 2 corone',3:'🔥 Drago — perdi 3 corone',4:'✨ Bellezza — nessun effetto',5:'🌸 Fiori — nessun effetto',6:'🦋 Farfalle — nessun effetto',7:'🛡 Scudo — +1 scudo',8:'↩ Corona — dai 1 corona al precedente',9:'↪ Corona — dai 1 corona al successivo',10:'♛ Corona — +1 corona'};
      if (effectTexts[cv]) {
        setCardEffectText(effectTexts[cv]);
        setTimeout(() => setCardEffectText(null), 2000);
      }
      setDisplayedCards(prev => {
        if (!prev) return prev;
        const updated = [...prev];
        updated[cardIndex] = { ...updated[cardIndex], revealed: true };
        return updated;
      });
      setFeedback({ message: response.data.message, cardValue: response.data.card_value });
      triggerAnimations(response.data.message, response.data.card_value, response.data.player_name);
      if (broadcastChannelRef.current) {
        broadcastChannelRef.current.send({
          type: 'broadcast', event: 'player_action',
          payload: { type: 'card_pick', player_id: user.user_id, player_name: response.data.player_name, card_value: response.data.card_value, card_effect: effectMap[response.data.card_value] || null, message: response.data.other_message }
        });
      }
      await new Promise(resolve => setTimeout(resolve, 800));
      setRevealingCard(null);
      setTimeout(() => setFeedback(null), 2500);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pick card');
      setRevealingCard(null);
    } finally {
      setActionInProgress(false);
    }
  };

  const handleUseJolly = async () => {
    if (actionInProgress) return;
    ensureAudio();
    setActionInProgress(true);
    try {
      const response = await axios.post(`${API}/game/use-jolly`, { game_id: gameId, user_id: user.user_id });
      toast.success(response.data.message);
      playJollySound();
      triggerPlayerEffect(myPlayer?.username, 'jolly');
      setCardEffect('jolly_effect');
      setTimeout(() => setCardEffect(null), 2200);
      if (broadcastChannelRef.current) {
        const myName = myPlayer?.username || 'Unknown';
        broadcastChannelRef.current.send({
          type: 'broadcast', event: 'player_action',
          payload: { type: 'jolly', player_id: user.user_id, player_name: myName, card_effect: 'jolly_effect', message: `${myName} used a Jester!` }
        });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to use jolly');
    } finally {
      setActionInProgress(false);
    }
  };

  if (loading || !gameState) {
    return (
      <div style={{ width: '100dvw', height: '100dvh', position: 'relative', overflow: 'hidden', background: '#04060a' }}>
        <BattleField kingdom="Unknown" mobile={isMobile} dimmed />
        <div style={{ position: 'relative', zIndex: 2, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 14 }}>
          <motion.div
            animate={{ rotate: [0, 8, -8, 0], scale: [1, 1.08, 1] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            style={{ fontSize: 46, filter: 'drop-shadow(0 0 22px rgba(255,200,60,0.6))' }}
          >
            ⚔
          </motion.div>
          <div style={{ fontFamily: "'Cinzel',serif", fontSize: 16, fontWeight: 800, letterSpacing: '0.16em', color: '#e8d68a' }}>
            Le armate si schierano…
          </div>
        </div>
      </div>
    );
  }

  const players = gameState.players || [];
  const currentPlayer = players[gameState.current_turn];
  const myPlayer = players.find(p => p.user_id === user.user_id);
  const isMyTurn = currentPlayer?.user_id === user.user_id;
  const playersWhoPlayed = gameState.players_who_played || [];
  const hasActedThisRound = myPlayer ? playersWhoPlayed.includes(myPlayer.user_id) : false;
  const canAct = isMyTurn && !myPlayer?.is_frozen && !actionInProgress && !pendingCardRefresh && !hasActedThisRound;
  const myKingdom = myPlayer?.kingdom || 'Unknown';

  const getRelativePosition = (playerIndex) => {
    if (!myPlayer) return null;
    const myIndex = players.findIndex(p => p.user_id === myPlayer.user_id);
    const relativeIndex = (playerIndex - myIndex + players.length) % players.length;
    return ['bottom', 'right', 'top', 'left'][relativeIndex];
  };

  const positionedPlayers = { bottom: null, right: null, top: null, left: null };
  players.forEach((player, index) => {
    const position = getRelativePosition(index);
    if (position) positionedPlayers[position] = player;
  });

  const idxOf = (p) => players.findIndex(x => x.user_id === p.user_id);

  // How each standard is planted around the table in 3D space
  const seat = (pos) => {
    if (isMobile && !isLandscape) {
      return { top: 'translateZ(-70px)', left: 'rotateY(14deg)', right: 'rotateY(-14deg)', bottom: 'translateZ(16px)' }[pos];
    }
    return {
      top: 'translateZ(-230px) rotateX(8deg)',
      left: 'rotateY(21deg) translateZ(-30px)',
      right: 'rotateY(-21deg) translateZ(-30px)',
      bottom: 'translateZ(45px) rotateX(-5deg)',
    }[pos];
  };

  const feedbackBar = (compact) => (
    <div style={{ minHeight: compact ? 24 : 32, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <AnimatePresence mode="wait">
        {cardEffectText ? (
          <motion.div key="eft" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ type: 'spring', stiffness: 400, damping: 28 }}
            style={{ background: 'linear-gradient(145deg,rgba(10,8,4,0.97),rgba(22,15,5,0.97))', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,215,0,0.5)', borderRadius: 10, padding: compact ? '4px 12px' : '6px 16px', boxShadow: '0 0 20px rgba(255,180,0,0.22)' }}>
            <span style={{ color: '#fde68a', fontSize: compact ? 10 : 12, fontWeight: 700 }}>{cardEffectText}</span>
          </motion.div>
        ) : feedback ? (
          <motion.div key="efb" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ type: 'spring', stiffness: 400, damping: 28 }}
            style={{ background: 'rgba(20,16,10,0.9)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,215,0,0.18)', borderRadius: 10, padding: compact ? '4px 10px' : '6px 14px', color: '#d4c89a', fontSize: compact ? 10 : 12, fontWeight: 600 }}>
            {feedback.message}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );

  const jollyButton = (compact) => (
    myPlayer && myPlayer.jolly > 0 && canAct && (
      <motion.button
        data-testid={compact ? 'use-jolly-button-mobile' : 'use-jolly-button'}
        onClick={handleUseJolly}
        disabled={actionInProgress}
        whileHover={{ scale: 1.04 }} whileTap={{ scale: 0.95 }}
        animate={{ boxShadow: ['0 0 8px rgba(139,92,246,0.35)', '0 0 20px rgba(167,139,250,0.8)', '0 0 8px rgba(139,92,246,0.35)'] }}
        transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: compact ? '5px 11px' : '6px 16px',
          background: 'linear-gradient(145deg, #4c1d95, #7c3aed 60%, #3b0764)',
          border: '1px solid rgba(196,181,253,0.55)',
          borderRadius: 9, cursor: actionInProgress ? 'not-allowed' : 'pointer',
          opacity: actionInProgress ? 0.5 : 1,
        }}
      >
        <img src="/images/icons/jolly.png" alt="Giullare" style={{ width: compact ? 16 : 19, height: compact ? 16 : 19, objectFit: 'contain' }} draggable={false} />
        <span style={{ fontSize: compact ? 9 : 11, fontWeight: 800, color: '#ede9fe', letterSpacing: '0.05em', whiteSpace: 'nowrap', fontFamily: "'Cinzel',serif" }}>
          {compact ? 'Salta' : 'Salta il turno'}
        </span>
      </motion.button>
    )
  );

  return (
    <div
      style={{ width: '100dvw', height: '100dvh', position: 'relative', overflow: 'hidden', background: '#03050a' }}
      onClick={ensureAudio}
    >
      <BattleField kingdom={myKingdom} mobile={isMobile} />
      <MusicToggle />
      <GameRules />
      <Herald show={heraldTurn} text="È il tuo turno" tone="gold" />
      <Herald show={pendingCardRefresh} text="Nuovo assalto" tone="crimson" duration={1.2} testId="round-herald" />

      <motion.div
        animate={shakeControls}
        style={{
          position: 'relative', zIndex: 10, height: '100%',
          perspective: isMobile ? SCENE.perspectiveMobile : SCENE.perspective,
          perspectiveOrigin: '50% 38%',
        }}
      >
        {(!isMobile || isLandscape) && (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column', padding: isMobile ? '2px 6px' : '48px 30px 22px', gap: isMobile ? 4 : 6, transformStyle: 'preserve-3d' }}>
            <div style={{ display: 'flex', justifyContent: 'center', flexShrink: 0, transformStyle: 'preserve-3d' }}>
              {positionedPlayers.top && (
                <div style={{ transform: seat('top'), transformStyle: 'preserve-3d' }}>
                  <PlayerAvatar player={positionedPlayers.top} isActive={currentPlayer?.user_id === positionedPlayers.top.user_id} isCurrentUser={false} playerIndex={idxOf(positionedPlayers.top)} animEffect={playerEffects[positionedPlayers.top.username]} size="top" />
                </div>
              )}
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, minHeight: 0, transformStyle: 'preserve-3d' }}>
              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, transformStyle: 'preserve-3d' }}>
                {positionedPlayers.left && (
                  <div style={{ transform: seat('left'), transformStyle: 'preserve-3d' }}>
                    <PlayerAvatar player={positionedPlayers.left} isActive={currentPlayer?.user_id === positionedPlayers.left.user_id} isCurrentUser={false} playerIndex={idxOf(positionedPlayers.left)} animEffect={playerEffects[positionedPlayers.left.username]} size="top" />
                  </div>
                )}
              </div>

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, minWidth: 0 }}>
                <CardGrid cards={displayedCards || gameState.cards_on_table || []} onPickCard={handlePickCard} canPick={canAct} revealingCardIndex={revealingCard} mobile={isMobile} kingdom={myKingdom} />
                {feedbackBar(false)}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, transformStyle: 'preserve-3d' }}>
                {positionedPlayers.right && (
                  <div style={{ transform: seat('right'), transformStyle: 'preserve-3d' }}>
                    <PlayerAvatar player={positionedPlayers.right} isActive={currentPlayer?.user_id === positionedPlayers.right.user_id} isCurrentUser={false} playerIndex={idxOf(positionedPlayers.right)} animEffect={playerEffects[positionedPlayers.right.username]} size="top" />
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, flexShrink: 0, transformStyle: 'preserve-3d' }}>
              {jollyButton(false)}
              {positionedPlayers.bottom && (
                <div style={{ transform: seat('bottom'), transformStyle: 'preserve-3d' }}>
                  <PlayerAvatar player={positionedPlayers.bottom} isActive={currentPlayer?.user_id === positionedPlayers.bottom.user_id} isCurrentUser={true} playerIndex={idxOf(positionedPlayers.bottom)} animEffect={playerEffects[positionedPlayers.bottom.username]} boxWidth={isMobile ? 120 : 280} />
                </div>
              )}
            </div>
          </div>
        )}

        {isMobile && !isLandscape && (
          <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column', paddingTop: 52, transformStyle: 'preserve-3d' }}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', flexShrink: 0, height: 96, transformStyle: 'preserve-3d' }}>
              {positionedPlayers.top && (
                <div style={{ transform: seat('top'), transformStyle: 'preserve-3d' }}>
                  <PlayerAvatar player={positionedPlayers.top} isActive={currentPlayer?.user_id === positionedPlayers.top.user_id} isCurrentUser={false} compact playerIndex={idxOf(positionedPlayers.top)} animEffect={playerEffects[positionedPlayers.top.username]} />
                </div>
              )}
            </div>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center', minHeight: 0, gap: 4, padding: '0 4px', transformStyle: 'preserve-3d' }}>
              <div style={{ flexShrink: 0, width: 78, transformStyle: 'preserve-3d' }}>
                {positionedPlayers.left && (
                  <div style={{ transform: seat('left'), transformStyle: 'preserve-3d' }}>
                    <PlayerAvatar player={positionedPlayers.left} isActive={currentPlayer?.user_id === positionedPlayers.left.user_id} isCurrentUser={false} compact side sideLabel="left" playerIndex={idxOf(positionedPlayers.left)} animEffect={playerEffects[positionedPlayers.left.username]} />
                  </div>
                )}
              </div>

              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, minHeight: 0, minWidth: 0 }}>
                <CardGrid cards={displayedCards || gameState.cards_on_table || []} onPickCard={handlePickCard} canPick={canAct} revealingCardIndex={revealingCard} mobile kingdom={myKingdom} />
                {feedbackBar(true)}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexShrink: 0 }}>
                  {jollyButton(true)}
                </div>
              </div>

              <div style={{ flexShrink: 0, width: 78, transformStyle: 'preserve-3d' }}>
                {positionedPlayers.right && (
                  <div style={{ transform: seat('right'), transformStyle: 'preserve-3d' }}>
                    <PlayerAvatar player={positionedPlayers.right} isActive={currentPlayer?.user_id === positionedPlayers.right.user_id} isCurrentUser={false} compact side sideLabel="right" playerIndex={idxOf(positionedPlayers.right)} animEffect={playerEffects[positionedPlayers.right.username]} />
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', flexShrink: 0, height: 126, paddingBottom: 6, transformStyle: 'preserve-3d' }}>
              {positionedPlayers.bottom && (
                <div style={{ transform: seat('bottom'), transformStyle: 'preserve-3d' }}>
                  <PlayerAvatar player={positionedPlayers.bottom} isActive={currentPlayer?.user_id === positionedPlayers.bottom.user_id} isCurrentUser={true} compact playerIndex={idxOf(positionedPlayers.bottom)} animEffect={playerEffects[positionedPlayers.bottom.username]} />
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>

      <CardEffectOverlay effect={cardEffect} positionedPlayers={positionedPlayers} />

      <AnimatePresence>
        {showIntro && (
          <BattleIntro
            kingdom={myKingdom}
            players={players.map(p => p.username)}
            onDone={() => { sessionStorage.setItem(`tlk_intro_${gameId}`, 'done'); setShowIntro(false); ensureAudio(); }}
          />
        )}
      </AnimatePresence>

      {gameState.status === 'finished' && showWinner && (
        <VictoryScreen
          winner={players.find(p => p.user_id === gameState.winner)}
          isMe={players.find(p => p.user_id === gameState.winner)?.user_id === user.user_id}
          winnerKingdom={players.find(p => p.user_id === gameState.winner)?.kingdom || 'Unknown'}
          onReturn={() => navigate('/games')}
        />
      )}
    </div>
  );
};

export default GameTable;
